import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Button, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { JournalText, CalendarDate, Person, Facebook, Twitter, Instagram, Linkedin } from 'react-bootstrap-icons';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import SEOHead from './SEOHead';
import whiteLogo from '../assets/whitebglogo.png';
import androidQrCode from '../assets/playstoreqr.png';
import iosQrCode from '../assets/iosqrcode.png';
import '../App.css';

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        // Avoid composite index requirement by skipping orderBy in Firestore
        const q = query(
          collection(db, 'blogs'),
          where('status', '==', 'published')
        );
        const snap = await getDocs(q);
        const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        // Sort client-side by date (supports Firestore Timestamp or YYYY-MM-DD string)
        items.sort((a, b) => {
          const toTs = (val) => {
            if (!val) return 0;
            if (val.seconds) return val.seconds * 1000;
            // try parsing ISO-like date string
            const parsed = Date.parse(val);
            return isNaN(parsed) ? 0 : parsed;
          };
          return toTs(b.date) - toTs(a.date);
        });
        setPosts(items);
      } catch (e) {
        console.error('Failed to load blogs', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <div className="modern-homepage">
      <SEOHead
        title="CBSE Study Blog – Tips, Guides, Sample Paper Strategies"
        description="Curated CBSE blog posts: exam strategies, subject guides, book lists, and study tips to help you learn better and score higher."
        keywords="CBSE blog, CBSE tips, exam strategy, study guide, sample papers"
        canonical="/blog"
        ogImage="/favicon.ico"
        ogType="website"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          itemListElement: posts.map((p, idx) => ({
            '@type': 'ListItem',
            position: idx + 1,
            url: `${window.location.origin}/blog/${p.slug || p.id}`,
            name: p.title,
          }))
        }}
      />
      <section className="guide-header-section">
        <Container>
          <Card className="guide-header-card">
            <div className="d-flex align-items-start justify-content-between flex-wrap gap-3">
              <div>
                <span className="guide-badge d-inline-flex align-items-center gap-2">
                  <JournalText /> Blog
                </span>
                <h1 className="guide-title">Insights, Tips, and Study Strategies</h1>
                <p className="guide-subtitle">Curated articles to help CBSE students learn better and score higher.</p>
                <p className="guide-description mb-0">
                  Explore practical guidance, book lists, sample paper tactics, exam hacks, and more — all aligned with
                  CBSE patterns. New posts every few weeks.
                </p>
              </div>
            </div>
          </Card>
        </Container>
      </section>

      <section className="subjects-section">
        <Container>
          <h2 className="section-title">Latest Posts</h2>
          {loading && (
            <div className="text-center my-5">
              <Spinner animation="border" variant="primary" />
            </div>
          )}
          <Row>
            {posts.map((post) => (
              <Col key={post.id} md={6} lg={4} className="mb-4">
                <Card className="resource-card h-100">
                  <Card.Body className="d-flex flex-column">
                    <div className="resource-card-header">
                      <div className="resource-icon">
                        <JournalText className="resource-icon-svg" />
                      </div>
                      <h3 className="resource-title mb-0">{post.title}</h3>
                    </div>
                    <div className="resource-card-content">
                      <p className="resource-description">{post.excerpt}</p>
                    </div>

                    <div className="d-flex align-items-center justify-content-between text-muted mb-3">
                      <div className="d-flex align-items-center gap-2">
                        <CalendarDate />
                        <small>{post.date && (post.date.seconds ? new Date(post.date.seconds * 1000).toLocaleDateString() : post.date)}</small>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <Person />
                        <small>{post.author}</small>
                      </div>
                    </div>

                    <div className="mt-auto">
                      <Button as={Link} to={`/blog/${post.slug || post.id}`} variant="primary" className="resource-btn w-100">
                        Read Post
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Footer */}
      <footer className="cubegon-footer">
        <div className="cubegon-footer-content">
          <div className="cubegon-footer-main">
            <div className="cubegon-footer-logo">
              <img src={whiteLogo} alt="Cubegon" />
            </div>

            <div className="cubegon-footer-download">
              <h3>Download the App</h3>
              <div className="cubegon-footer-qr-container">
                <div className="cubegon-footer-qr">
                  <a 
                    href="https://play.google.com/store/apps/details?id=com.cubegon.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ display: 'block', textDecoration: 'none' }}
                  >
                    <img src={androidQrCode} alt="Android QR code - Download from Play Store" />
                    <p style={{ textAlign: 'center', marginTop: '8px', fontSize: '12px', color: '#fff' }}>Android</p>
                  </a>
                </div>
                <div className="cubegon-footer-qr">
                  <a 
                    href="https://apps.apple.com/in/app/cubegon-smart-learning/id6752596017" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ display: 'block', textDecoration: 'none' }}
                  >
                    <img src={iosQrCode} alt="iOS QR code - Download from App Store" />
                    <p style={{ textAlign: 'center', marginTop: '8px', fontSize: '12px', color: '#fff' }}>iOS</p>
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="cubegon-footer-bottom">
            <div className="cubegon-footer-social">
              <a aria-label="Facebook" href="https://facebook.com/cubegon" target="_blank" rel="noopener noreferrer">
                <Facebook />
              </a>
              <a aria-label="Twitter" href="https://twitter.com/cubegon" target="_blank" rel="noopener noreferrer">
                <Twitter />
              </a>
              <a aria-label="Instagram" href="https://instagram.com/cubegon.app" target="_blank" rel="noopener noreferrer">
                <Instagram />
              </a>
              <a aria-label="LinkedIn" href="https://linkedin.com/company/cubegon" target="_blank" rel="noopener noreferrer">
                <Linkedin />
              </a>
            </div>

            <p className="cubegon-footer-text">Made in 🇮🇳 with ❤️</p>
            <p className="cubegon-footer-copyright">© {new Date().getFullYear()} Numeric Education Pvt. Ltd. All rights reserved.</p>
            <div className="cubegon-footer-links">
              <a href="https://cubegon.app/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
              <a href="https://cubegon.app/terms-and-conditions" target="_blank" rel="noopener noreferrer">Terms & Conditions</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Blog;


